import { useLongPress } from '@affine/core/components/hooks/use-long-press';
import { useNavigateHelper } from '@affine/core/components/hooks/use-navigate-helper';
import { EditorSettingService } from '@affine/core/modules/editor-setting';
import {
  type QuickSearchItem,
  QuickSearchService,
} from '@affine/core/modules/quicksearch';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { WorkspacesService } from '@affine/core/modules/workspace';
import { UNTITLED_WORKSPACE_NAME } from '@affine/env/constant';
import { LockIcon } from '@blocksuite/icons/rc';
import {
  LiveData,
  useLiveData,
  useService,
  useServiceOptional,
} from '@toeverything/infra';
import { useMemo } from 'react';

type PrivateWorkspaceItem = QuickSearchItem<
  'private-workspace',
  { workspaceId: string }
>;

/** Ids of workspaces hidden from the workspace switcher. */
export function usePrivateWorkspaces() {
  return useLiveData(
    useService(EditorSettingService).editorSetting.privateWorkspaces.$
  );
}

/** Long-press handlers for a workspace switcher, held for the configured time. */
export function usePrivateWorkspacesLongPress(onLongPress?: () => void) {
  const seconds = useLiveData(
    useService(EditorSettingService).editorSetting
      .privateWorkspacesLongPressSeconds.$
  );
  return useLongPress(onLongPress, seconds * 1000);
}

/**
 * Returns a callback that lists private workspaces in the quick-search modal
 * and switches to the picked one. `undefined` outside a workspace, where the
 * modal is not mounted.
 */
export function useOpenPrivateWorkspaces() {
  const quickSearch = useServiceOptional(QuickSearchService)?.quickSearch;
  const workspacesService = useService(WorkspacesService);
  const editorSetting = useService(EditorSettingService).editorSetting;
  const workbench = useServiceOptional(WorkbenchService)?.workbench;
  const { jumpToPage } = useNavigateHelper();

  return useMemo(() => {
    if (!quickSearch) return undefined;
    return () => {
      const list = workspacesService.list;
      list.revalidate();
      const ids = editorSetting.privateWorkspaces.value;
      list.workspaces$.value
        .filter(meta => ids.includes(meta.id))
        .forEach(meta => workspacesService.getProfile(meta).revalidate());

      const query$ = new LiveData('');
      const items$ = LiveData.computed<PrivateWorkspaceItem[]>(get => {
        const privateIds = get(editorSetting.privateWorkspaces.$);
        const query = get(query$).trim().toLowerCase();
        return get(list.workspaces$)
          .filter(meta => privateIds.includes(meta.id))
          .map(meta => ({
            id: meta.id,
            name:
              get(workspacesService.getProfile(meta).profile$)?.name ||
              UNTITLED_WORKSPACE_NAME,
          }))
          .filter(({ name }) => name.toLowerCase().includes(query))
          .map(({ id, name }) => ({
            id: 'private-workspace:' + id,
            source: 'private-workspace',
            label: { title: name },
            icon: LockIcon,
            payload: { workspaceId: id },
          }));
      });

      quickSearch.show(
        [{ items$, query: (q: string) => query$.next(q) }],
        result => {
          if (!result) return;
          // same as the workspace switcher: drop split views of this workspace
          workbench?.views$.value.forEach(view => {
            if (workbench.activeView$.value !== view) workbench.close(view);
          });
          jumpToPage(result.payload.workspaceId, 'notes');
        },
        {
          label: { i18nKey: 'com.affine.private-workspaces.title' },
          placeholder: { i18nKey: 'com.affine.private-workspaces.placeholder' },
        }
      );
    };
  }, [editorSetting, jumpToPage, quickSearch, workbench, workspacesService]);
}
